import React, { useState, useMemo } from 'react';
import {
  Card,
  Box,
  Text,
  VStack,
  HStack,
  Heading,
  Button,
  Badge,
  BadgeText,
  useToken,
} from '@gluestack-ui/themed';
import { ChevronRight } from 'lucide-react-native';
import HtmlText from '@/common/components/HtmlText';
import type { Task } from '@/common/api/models/task';
import { useEvaluationGradingConfig } from '@/lecturer/EvaluationDesk/context/EvaluationGradingConfigContext';
import type { EvaluationDimension } from '@/common/api/models/evaluationDimension';
import SubmissionDetailsModal from '@/student/grade/components/parts/SubmissionDetailsModal';
import {
  convertEvaluationsToPerformances,
  calculateOralExamPerformance,
  calculateTaskPerformance,
  formatPerformanceLevelDisplay,
} from '@/lecturer/EvaluationDesk/utils/gradingConversions';
import {
  getFlatEvaluationDimensions,
  isOralExamTaskEvaluation,
} from '@/common/utils/evaluation/getFlatEvaluationDimensions';

interface ExtendedEvaluationDimension extends EvaluationDimension {
  level?: string;
  comment?: string;
}

export const ElegantLevelIndicator: React.FC<{
  level: string;
  size?: 'sm' | 'md';
  color?: string;
}> = ({ level, size = 'sm', color = '#666666' }) => {
  const getFilledBars = (levelId: string): number => {
    switch (levelId) {
      case 'very_good':
        return 4;
      case 'good':
        return 3;
      case 'satisfactory':
        return 2;
      case 'needs_improvement':
        return 1;
      default:
        return 0;
    }
  };

  const filledBars = getFilledBars(level);

  const barWidth = size === 'sm' ? 6 : 8;
  const spacing = size === 'sm' ? 'xs' : 'sm';
  const barHeight = size === 'sm' ? 2 : 3;

  return (
    <HStack space={spacing} alignItems="center" ml="$1">
      {[1, 2, 3, 4].map((barNum) => {
        const isActive = (barNum - 1) < filledBars;

        return (
          <Box
            key={`bar-${barNum}`}
            width={barWidth}
            height={barHeight}
            bg={color}
            opacity={isActive ? 1 : 0.2}
            borderRadius="$full"
          />
        );
      })}
    </HStack>
  );
};

const calculateGradeImpact = (
  task: Task,
  levelWeights: Record<string, number>,
  allTasks: Task[] = [],
): string => {
  if (!task.submission?.evaluation) return '0.0';

  const calculateGradeFromEvaluation = (evaluation: Record<string, ExtendedEvaluationDimension>) => {
    if (!evaluation) return null;

    const grades = Object.entries(evaluation).reduce((acc, [dimension, evalObj]) => {
      if (evalObj && evalObj.level && evalObj.level !== 'n/a') {
        const weight = levelWeights[evalObj.level] || 0;
        acc[dimension] = 4 - weight * 3;
      }
      return acc;
    }, {} as Record<string, number | null>);

    const formalGrade = grades.formal || 0;
    const otherGrades = Object.entries(grades)
      .filter(([dim]) => dim !== 'formal')
      .map(([, grade]) => grade)
      .filter((grade) => grade !== null) as number[];

    const otherGradeAvg = otherGrades.length > 0 ? otherGrades.reduce((sum, grade) => sum + grade, 0) / otherGrades.length : 0;

    const taskGrade = formalGrade && otherGradeAvg ? 0.2 * formalGrade + 0.8 * otherGradeAvg : formalGrade || otherGradeAvg || 0;

    return taskGrade;
  };

  const taskGrade = calculateGradeFromEvaluation(
    getFlatEvaluationDimensions(task.submission.evaluation) as Record<string, ExtendedEvaluationDimension>,
  );
  if (!taskGrade) return '0.0';

  const tasksWithEvals = allTasks.filter((t) => t.submission?.evaluation);

  const transferTasks = tasksWithEvals.filter((t) => t.gradingSystem === 'full_evaluation');
  const regularTasks = tasksWithEvals.filter((t) => t.gradingSystem !== 'full_evaluation');

  const transferGrades = transferTasks
    .map((t) => calculateGradeFromEvaluation(
      getFlatEvaluationDimensions(t.submission?.evaluation) as Record<string, ExtendedEvaluationDimension>,
    ))
    .filter((g) => g !== null) as number[];

  const regularGrades = regularTasks
    .map((t) => calculateGradeFromEvaluation(
      getFlatEvaluationDimensions(t.submission?.evaluation) as Record<string, ExtendedEvaluationDimension>,
    ))
    .filter((g) => g !== null) as number[];

  const transferAvg = transferGrades.length > 0 ? transferGrades.reduce((sum, g) => sum + g, 0) / transferGrades.length : 0;

  const regularAvg = regularGrades.length > 0 ? regularGrades.reduce((sum, g) => sum + g, 0) / regularGrades.length : 0;

  let baseline = 3.0;

  if (transferGrades.length > 0 && regularGrades.length > 0) {
    baseline = 0.75 * transferAvg + 0.25 * regularAvg;
  } else if (transferGrades.length > 0) {
    baseline = transferAvg;
  } else if (regularGrades.length > 0) {
    baseline = regularAvg;
  }

  const taskType = task.gradingSystem === 'full_evaluation' ? 'transfer' : 'regular';
  const scaleFactor = taskType === 'transfer' ? 0.75 : 0.25;

  const totalTransferTasks = transferTasks.length || 1;
  const totalRegularTasks = regularTasks.length || 1;
  const taskCount = taskType === 'transfer' ? totalTransferTasks : totalRegularTasks;

  let impact = ((taskGrade - baseline) * scaleFactor) / taskCount;

  impact = -impact;

  return impact.toFixed(1);
};

const TaskFeedbackCard: React.FC<{ task: Task; allTasks: Task[] }> = ({ task, allTasks }) => {
  const [showDetails, setShowDetails] = useState(false);
  const {
    evalDimensionKeys,
    dimensionColors,
    levelWeights,
    dimensionMaxWeights,
    performanceLevels,
  } = useEvaluationGradingConfig();
  const blueColor = useToken('colors', 'blue500');

  const gradeImpact = useMemo(() => calculateGradeImpact(task, levelWeights, allTasks), [task, allTasks, levelWeights]);

  const flatEvaluationDimensions = useMemo(
    () => getFlatEvaluationDimensions(task.submission?.evaluation),
    [task.submission?.evaluation],
  );

  const presentDimensions = useMemo(() => {
    if (!task.submission?.evaluation) return [];
    return evalDimensionKeys.filter((dim) => Object.prototype.hasOwnProperty.call(flatEvaluationDimensions, dim));
  }, [task.submission?.evaluation, evalDimensionKeys, flatEvaluationDimensions]);

  const performances = convertEvaluationsToPerformances(flatEvaluationDimensions, levelWeights);

  const rawTaskPerformance = useMemo(() => {
    const evaluation = task.submission?.evaluation;
    if (task.type === 'oral_exam' && isOralExamTaskEvaluation(evaluation)) {
      return calculateOralExamPerformance(evaluation.subEvaluations, levelWeights);
    }
    return calculateTaskPerformance(performances, dimensionMaxWeights);
  }, [task.type, task.submission?.evaluation, performances, levelWeights, dimensionMaxWeights]);

  const performanceLevelDisplay = useMemo(
    () => formatPerformanceLevelDisplay(rawTaskPerformance, performanceLevels),
    [rawTaskPerformance, performanceLevels],
  );

  const hasApplicableDimension = useMemo(() => {
    const evaluation = task.submission?.evaluation;
    if (task.type === 'oral_exam' && isOralExamTaskEvaluation(evaluation)) {
      const rows = evaluation.subEvaluations
        ? (Array.isArray(evaluation.subEvaluations)
          ? evaluation.subEvaluations
          : Object.values(evaluation.subEvaluations))
        : [];
      return rows.some((sub) => {
        const dimGrades = sub?.dimGrades ?? {};
        return Object.values(dimGrades).some((rating) => {
          const level = typeof rating === 'string'
            ? rating
            : (rating && typeof rating === 'object' && 'level' in rating
              && typeof rating.level === 'string'
              ? rating.level
              : undefined);
          return !!level && level !== 'n/a' && level !== 'not_applicable';
        });
      });
    }
    return Object.values(flatEvaluationDimensions).some((ev) => ev?.level && ev.level !== 'n/a');
  }, [task.type, task.submission?.evaluation, flatEvaluationDimensions]);
  const feedbackText = task.submission?.feedback?.text?.trim() || '';

  return (
    <Card p="$4">
      <VStack space="md">
        <HStack justifyContent="space-between">
          <Heading size="md">{task.title}</Heading>
          {hasApplicableDimension && performanceLevelDisplay && (
            <Badge variant="outline" borderColor="$borderLight" testID="task-level-badge">
              <HStack space="xs" alignItems="center">
                <BadgeText color="$textSubtle" size="xs">
                  {performanceLevelDisplay}
                </BadgeText>
              </HStack>
            </Badge>
          )}
        </HStack>
        {feedbackText && (
          <Box>
            <HtmlText
              content={feedbackText}
              fontSize={14}
              contentStyle={{}}
              numberOfLines={0}
              applySubmissionFormatting={false}
            />
          </Box>
        )}
        {!feedbackText && (
          <Text size="sm" color="$textSubtle">
            No written feedback yet.
          </Text>
        )}

        <HStack justifyContent="space-between" alignItems="center" mt="$2">
          <HStack space="xs" alignItems="center">
            {task.submission?.evaluation && presentDimensions.length > 0 && (
              <HStack space="xs" flexWrap="wrap" flex={1}>
                {presentDimensions.map((dim) => {
                  const dimEvaluation = flatEvaluationDimensions[dim] as ExtendedEvaluationDimension | undefined;
                  if (!dimEvaluation?.level || dimEvaluation.level === 'n/a') return null;

                  const dimensionColor = dimensionColors[dim] || '#666666';

                  return (
                    <Badge
                      key={dim}
                      testID={`dim-badge-${dim}`}
                      borderLeftWidth={2}
                      borderLeftColor={dimensionColor}
                      bg="transparent"
                      mb="$1"
                      borderRadius={0}
                      px="$2"
                      py="$1"
                    >
                      <VStack space="xs" w={70}>
                        <Text size="2xs" color="$textSubtle">
                          {dim.toUpperCase()}
                        </Text>
                        <ElegantLevelIndicator level={dimEvaluation.level} size="sm" color={dimensionColor} />
                      </VStack>
                    </Badge>
                  );
                })}
              </HStack>
            )}
          </HStack>
          {Math.abs(parseFloat(gradeImpact)) > 0.1 && (
            <Box mr="$2">
              <Badge variant="outline" bg="$backgroundLight50" borderColor="$borderLight200">
                <HStack space="xs" alignItems="center">
                  <BadgeText color="$textSubtle" size="xs">
                    Grade Impact:
                    {' '}
                    {Math.abs(parseFloat(gradeImpact)).toFixed(1)}
                  </BadgeText>
                  <Text
                    fontSize={16}
                    color={parseFloat(gradeImpact) > 0 ? '$success600' : '$error600'}
                    fontWeight="bold"
                  >
                    {parseFloat(gradeImpact) > 0 ? '↑' : '↓'}
                  </Text>
                </HStack>
              </Badge>
            </Box>
          )}

          <Button variant="link" onPress={() => setShowDetails(true)} ml="$2">
            <HStack space="xs" alignItems="center">
              <Text size="sm" color="$blue500">
                View Details
              </Text>
              <ChevronRight size={16} color={blueColor} />
            </HStack>
          </Button>
        </HStack>

        <SubmissionDetailsModal
          visible={showDetails}
          onClose={() => setShowDetails(false)}
          title={task.title}
          description={task.description}
          submissionText={task.submission?.text}
          submissionLabel="Your Submission"
          attachments={task.submission?.attachments}
        />
      </VStack>
    </Card>
  );
};

export default TaskFeedbackCard;
